import React from 'react';
import PropTypes from 'prop-types';
import { Button } from 'reactstrap';
import classes from './ECB.module.scss';
import { IconExternalLink } from '@tabler/icons-react';

const ECB = ({ assets, altText, link, index }) => {
    const showBtn = link?.buttonVisibility && link?.buttonText;
    const linkOnly = link?.linkUrl && !showBtn;

    const btnPosSm = (link?.buttonPositioning?.sm || 'center').toLowerCase();
    const btnPosMd = (link?.buttonPositioning?.md || btnPosSm).toLowerCase();

    const posClassMapSm = {
        left: classes.posSm_left,
        right: classes.posSm_right,
        center: classes.posSm_center,
        fullwidth: classes.posSm_fullwidth,
    };
    const posClassMapMd = {
        left: classes.posMd_left,
        right: classes.posMd_right,
        center: classes.posMd_center,
        fullwidth: classes.posMd_fullwidth,
    };

    const overlayClassName = [
        classes.ECBOverlay,
        posClassMapSm[btnPosSm] || classes.posSm_center,
        posClassMapMd[btnPosMd] || classes.posMd_center,
    ].join(' ');

    const isCustomBg = link?.buttonColor === 'custom' && link?.customButtonColor;
    const bsColorProp = isCustomBg ? undefined : link?.buttonColor || 'primary';

    const textMode = link?.buttonTextColor?.mode || 'dark';
    const textClass = textMode === 'light' ? 'text-light' : textMode === 'dark' ? 'text-dark' : undefined;

    const inlineTextColor =
        textMode === 'custom' && link?.buttonTextColor?.custom ? { color: link.buttonTextColor.custom } : undefined;

    const Img = (
        <picture>
            <source media="(max-width: 767px)" srcSet={assets?.sm} />
            <source media="(min-width: 768px)" srcSet={assets?.md || assets?.sm} />
            <img src={assets?.md || assets?.sm} alt={altText} className={`${classes.ECBImage} img-fluid`} />
        </picture>
    );

    const ButtonNode = showBtn ? (
        <div className={overlayClassName}>
            <Button
                color={bsColorProp}
                outline={link?.outline}
                href={link?.linkUrl || undefined}
                target={link?.linkTarget || undefined}
                rel={link?.linkTarget === '_blank' ? 'noopener' : undefined}
                className={`${classes.ECBBtn} anl_ecb--button-${index}${textClass ? ` ${textClass}` : ''}${link?.outline ? ` ${classes['is-outline']}` : ''}`}
                style={{
                    // Solid custom color: match bg and border
                    ...(!link?.outline && isCustomBg
                        ? { backgroundColor: link.customButtonColor, borderColor: link.customButtonColor }
                        : null),
                    // Outline custom color: set only color + border-color; leave width/style to Bootstrap
                    ...(link?.outline && isCustomBg
                        ? {
                              color: inlineTextColor?.color || link.customButtonColor,
                              borderColor: link.customButtonColor,
                              backgroundColor: 'transparent',
                          }
                        : null),
                    // Only apply custom text color for non-outline
                    ...(inlineTextColor && !link?.outline ? inlineTextColor : null),
                }}
            >
                {link?.buttonText}
                {/https?:\/\/.*/.test(link.linkUrl) && <IconExternalLink size={20} className="ms-1" />}
            </Button>
        </div>
    ) : null;

    const Content = (
        <>
            {Img}
            {ButtonNode}
        </>
    );

    return linkOnly ? (
        <a
            aria-label={
                link?.linkTarget === '_blank'
                    ? `Zur Seite ${link.linkUrl} (wird in neuem Tab geöffnet)`
                    : `Zur Seite ${link.linkUrl}`
            }
            href={link.linkUrl}
            target={link.linkTarget}
            rel="noopener"
            className={`${classes.ECBCard} anl_ecb--link-${index} d-block text-decoration-none`}
        >
            {Content}
        </a>
    ) : (
        <div className={`${classes.ECBCard}`}>{Content}</div>
    );
};

ECB.propTypes = {
    assets: PropTypes.shape({
        sm: PropTypes.string,
        md: PropTypes.string,
    }).isRequired,
    altText: PropTypes.string.isRequired,
    link: PropTypes.shape({
        linkUrl: PropTypes.string,
        linkTarget: PropTypes.oneOf(['_blank', '_self']),
        buttonText: PropTypes.string,
        buttonColor: PropTypes.oneOf(['primary', 'secondary', 'custom']),
        customButtonColor: PropTypes.string,
        buttonTextColor: PropTypes.shape({
            mode: PropTypes.oneOf(['dark', 'light', 'custom']),
            custom: PropTypes.string,
        }),
        outline: PropTypes.bool,
        buttonVisibility: PropTypes.bool,
        buttonPositioning: PropTypes.shape({
            sm: PropTypes.oneOf(['left', 'center', 'right', 'fullwidth']),
            md: PropTypes.oneOf(['left', 'center', 'right', 'fullwidth']),
        }),
    }),
    index: PropTypes.number,
};

export default ECB;
